import React, { useMemo } from "react";
import { ProcessedData } from "./ChartDataProcessor";
import { ViewType } from "./TimeNavigation";
import { EnhancedLineChart } from "./EnhancedLineChart";
import { CombinedLineChart } from "./CombinedLineChart";
import {
  ChartGrid,
  ChartCard,
  ChartContainer,
} from "styles/devices/deviceCharts.styles";
import {
  ListGrid,
  ListChartCard,
  ListChartContainer,
  CombinedChartWrapper,
  CombinedContainer,
} from "styles/devices/deviceChartsNavigation.styles";
import { CHART_COLORS } from "./chartColors";

interface ChartRendererProps {
  processedData: ProcessedData[];
  dates: string[];
  fullDates: string[];
  getRange: (name: string) => { min?: number; max?: number };
  temperatureUnit: string;
  vsdwUnit: string;
  viewType: ViewType;
  timeRangeLabel: string;
  fromTimestamp?: number;
  toTimestamp?: number;
  zoomKey?: number;
  onResetZoom?: () => void;
  onViewportChange?: (minTimestamp: number) => void;
}

// Fixed Y-axis ranges per sensor — keeps charts stable regardless of data
const FIXED_RANGES: Record<string, { min: number; max: number }> = {
  pH: { min: 0, max: 14 },
  ORP: { min: 0, max: 1000 },
  Temperature: { min: 0, max: 60 },
  Salinity: { min: 0, max: 10000 },
  Flow: { min: 0, max: 1.5 },
  Vsdw: { min: 0, max: 50 },
};

export const ChartRenderer: React.FC<ChartRendererProps> = React.memo(
  ({
    processedData,
    dates,
    fullDates,
    getRange,
    temperatureUnit,
    vsdwUnit,
    viewType,
    timeRangeLabel,
    fromTimestamp,
    toTimestamp,
    zoomKey,
    onResetZoom,
    onViewportChange,
  }) => {
    // Memoize combined-view datasets to avoid 6x .map() on every render
    const combinedTimestamps = useMemo(
      () => processedData.map((d) => d.timestamp),
      [processedData],
    );
    const combinedDatasets = useMemo(
      () => [
        {
          name: "pH",
          data: processedData.map((d) => d.pH),
          color: CHART_COLORS.pH,
        },
        {
          name: "ORP",
          data: processedData.map((d) => d.ORP),
          color: CHART_COLORS.ORP,
          unit: "mV",
        },
        {
          name: "Temperature",
          data: processedData.map((d) => d.Temperature),
          color: CHART_COLORS.Temperature,
          unit: temperatureUnit,
        },
        {
          name: "Salinity",
          data: processedData.map((d) => d.Salinity),
          color: CHART_COLORS.Salinity,
          unit: "ppm",
        },
        {
          name: "Flow Status",
          data: processedData.map((d) => d.Flow),
          color: CHART_COLORS.Flow,
        },
        {
          name: "Vsdw",
          data: processedData.map((d) => d.Vsdw),
          color: CHART_COLORS.Vsdw,
          unit: vsdwUnit,
        },
      ],
      [processedData, temperatureUnit, vsdwUnit],
    );

    if (viewType === "combined") {
      return (
        <CombinedChartWrapper>
          <CombinedContainer>
            <CombinedLineChart
              labels={dates}
              fullDates={fullDates}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              timestamps={combinedTimestamps}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
              datasets={combinedDatasets}
            />
          </CombinedContainer>
        </CombinedChartWrapper>
      );
    }

    if (viewType === "list") {
      return (
        <ListGrid>
          <ListChartCard>
            <ListChartContainer>
              <EnhancedLineChart
                title="pH Levels"
                processedData={processedData}
                dataType="pH"
                color={CHART_COLORS.pH}
                range={FIXED_RANGES.pH}
                timeRangeLabel={timeRangeLabel}
                fromTimestamp={fromTimestamp}
                toTimestamp={toTimestamp}
                zoomKey={zoomKey}
                onResetZoom={onResetZoom}
                onViewportChange={onViewportChange}
              />
            </ListChartContainer>
          </ListChartCard>

          <ListChartCard>
            <ListChartContainer>
              <EnhancedLineChart
                title="ORP Levels"
                processedData={processedData}
                dataType="ORP"
                color={CHART_COLORS.ORP}
                unit="mV"
                range={FIXED_RANGES.ORP}
                timeRangeLabel={timeRangeLabel}
                fromTimestamp={fromTimestamp}
                toTimestamp={toTimestamp}
                zoomKey={zoomKey}
                onResetZoom={onResetZoom}
                onViewportChange={onViewportChange}
              />
            </ListChartContainer>
          </ListChartCard>

          <ListChartCard>
            <ListChartContainer>
              <EnhancedLineChart
                title="Temperature"
                processedData={processedData}
                dataType="Temperature"
                color={CHART_COLORS.Temperature}
                unit={temperatureUnit}
                range={FIXED_RANGES.Temperature}
                timeRangeLabel={timeRangeLabel}
                fromTimestamp={fromTimestamp}
                toTimestamp={toTimestamp}
                zoomKey={zoomKey}
                onResetZoom={onResetZoom}
                onViewportChange={onViewportChange}
              />
            </ListChartContainer>
          </ListChartCard>

          <ListChartCard>
            <ListChartContainer>
              <EnhancedLineChart
                title="Salinity"
                processedData={processedData}
                dataType="Salinity"
                color={CHART_COLORS.Salinity}
                unit="ppm"
                range={FIXED_RANGES.Salinity}
                timeRangeLabel={timeRangeLabel}
                fromTimestamp={fromTimestamp}
                toTimestamp={toTimestamp}
                zoomKey={zoomKey}
                onResetZoom={onResetZoom}
                onViewportChange={onViewportChange}
              />
            </ListChartContainer>
          </ListChartCard>

          <ListChartCard>
            <ListChartContainer>
              <EnhancedLineChart
                title="Flow Status"
                processedData={processedData}
                dataType="Flow"
                color={CHART_COLORS.Flow}
                timeRangeLabel={timeRangeLabel}
                fromTimestamp={fromTimestamp}
                toTimestamp={toTimestamp}
                zoomKey={zoomKey}
                onResetZoom={onResetZoom}
                onViewportChange={onViewportChange}
                customYAxis={{
                  min: 0,
                  max: 1.5,
                  ticks: {
                    callback: function (this: any, value: any) {
                      return value === 0 ? "Off" : "On";
                    },
                  },
                }}
              />
            </ListChartContainer>
          </ListChartCard>

          <ListChartCard>
            <ListChartContainer>
              <EnhancedLineChart
                title="Vsdw"
                processedData={processedData}
                dataType="Vsdw"
                color={CHART_COLORS.Vsdw}
                unit={vsdwUnit}
                range={FIXED_RANGES.Vsdw}
                timeRangeLabel={timeRangeLabel}
                fromTimestamp={fromTimestamp}
                toTimestamp={toTimestamp}
                zoomKey={zoomKey}
                onResetZoom={onResetZoom}
                onViewportChange={onViewportChange}
              />
            </ListChartContainer>
          </ListChartCard>
        </ListGrid>
      );
    }

    // Default: grid view
    return (
      <ChartGrid>
        <GridCharts
          processedData={processedData}
          dates={dates}
          fullDates={fullDates}
          getRange={getRange}
          temperatureUnit={temperatureUnit}
          vsdwUnit={vsdwUnit}
          timeRangeLabel={timeRangeLabel}
          fromTimestamp={fromTimestamp}
          toTimestamp={toTimestamp}
          zoomKey={zoomKey}
          onResetZoom={onResetZoom}
          onViewportChange={onViewportChange}
        />
      </ChartGrid>
    );
  },
);

// Grid charts component
interface GridChartsProps {
  processedData: ProcessedData[];
  dates: string[];
  fullDates: string[];
  getRange: (name: string) => { min?: number; max?: number };
  temperatureUnit: string;
  vsdwUnit: string;
  timeRangeLabel: string;
  fromTimestamp?: number;
  toTimestamp?: number;
  zoomKey?: number;
  onResetZoom?: () => void;
  onViewportChange?: (minTimestamp: number) => void;
}

const GridCharts: React.FC<GridChartsProps> = React.memo(
  ({
    processedData,
    dates,
    fullDates,
    getRange,
    temperatureUnit,
    vsdwUnit,
    timeRangeLabel,
    fromTimestamp,
    toTimestamp,
    zoomKey,
    onResetZoom,
    onViewportChange,
  }) => {
    return (
      <>
        <ChartCard>
          <ChartContainer>
            <EnhancedLineChart
              title="pH Levels"
              processedData={processedData}
              dataType="pH"
              color={CHART_COLORS.pH}
              range={FIXED_RANGES.pH}
              timeRangeLabel={timeRangeLabel}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
            />
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartContainer>
            <EnhancedLineChart
              title="ORP Levels"
              processedData={processedData}
              dataType="ORP"
              color={CHART_COLORS.ORP}
              unit="mV"
              range={FIXED_RANGES.ORP}
              timeRangeLabel={timeRangeLabel}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
            />
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartContainer>
            <EnhancedLineChart
              title="Temperature"
              processedData={processedData}
              dataType="Temperature"
              color={CHART_COLORS.Temperature}
              unit={temperatureUnit}
              range={FIXED_RANGES.Temperature}
              timeRangeLabel={timeRangeLabel}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
            />
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartContainer>
            <EnhancedLineChart
              title="Salinity"
              processedData={processedData}
              dataType="Salinity"
              color={CHART_COLORS.Salinity}
              unit="ppm"
              range={FIXED_RANGES.Salinity}
              timeRangeLabel={timeRangeLabel}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
            />
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartContainer>
            <EnhancedLineChart
              title="Flow Status"
              processedData={processedData}
              dataType="Flow"
              color={CHART_COLORS.Flow}
              timeRangeLabel={timeRangeLabel}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
              customYAxis={{
                min: 0,
                max: 1.5,
                ticks: {
                  callback: function (this: any, value: any) {
                    return value === 0 ? "Off" : "On";
                  },
                },
              }}
            />
          </ChartContainer>
        </ChartCard>

        <ChartCard>
          <ChartContainer>
            <EnhancedLineChart
              title="Vsdw"
              processedData={processedData}
              dataType="Vsdw"
              color={CHART_COLORS.Vsdw}
              unit={vsdwUnit}
              range={FIXED_RANGES.Vsdw}
              timeRangeLabel={timeRangeLabel}
              fromTimestamp={fromTimestamp}
              toTimestamp={toTimestamp}
              zoomKey={zoomKey}
              onResetZoom={onResetZoom}
              onViewportChange={onViewportChange}
            />
          </ChartContainer>
        </ChartCard>
      </>
    );
  },
);
